/**
 * component-personalization.js — <product-personalization>
 *
 * Live SVG preview, character-set validation, and derived width limits.
 *
 * SVG rather than canvas or a server round-trip: it is instant, resolution
 * independent, needs no network call per keystroke, and the rendered text
 * stays real text that assistive technology can read.
 *
 * This preview is the ONLY representation the customer ever sees before the
 * item is printed. No proof is emailed after purchase. Treat any bug in here as
 * a bug that ships wrong product, not as a cosmetic issue.
 *
 * The whole component is an enhancement. Without JavaScript the input, the
 * maxlength, the required attributes and the line-item properties all still
 * work — the customer just does not get a preview.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 150;
  const MM_PER_INCH = 25.4;

  /**
   * Character sets.
   *
   * `extended` is the DEFAULT and includes Latin-1 Supplement / Latin
   * Extended-A. On a Canadian store selling name signs this is not an edge
   * case: é è ê ë à â ç ô î ï û ù œ appear in everyday names, and rejecting
   * or silently stripping them produces a guaranteed refund.
   */
  const CHARSETS = {
    // A–Z, digits, space and the punctuation that appears in real names.
    basic: /^[A-Za-z0-9 .,'&!?\-]*$/,
    // Any Latin-script letter plus marks, digits, spaces, punctuation.
    extended: /^[\p{Script=Latin}\p{M}\p{N}\p{Zs}.,'&!?\-]*$/u
  };

  const EMOJI = /\p{Extended_Pictographic}/u;
  const ANY_LETTER = /\p{L}/u;
  const LATIN_LETTER = /\p{Script=Latin}/u;

  class Personalization extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-personalization-input]');
      if (!this.input) return;

      this.counter = this.querySelector('[data-personalization-count]');
      this.errorEl = this.querySelector('[data-personalization-error]');
      this.noticeEl = this.querySelector('[data-personalization-notice]');
      this.noticeText = this.querySelector('[data-personalization-notice-text]');
      this.previewWrap = this.querySelector('[data-preview-wrap]');
      this.svg = this.querySelector('[data-preview-svg]');
      this.svgText = this.querySelector('[data-preview-text]');
      this.svgTitle = this.querySelector('[data-preview-title]');

      this.fields = {
        normalized: this.querySelector('[data-personalization-normalized]'),
        charCount: this.querySelector('[data-personalization-charcount]'),
        width: this.querySelector('[data-personalization-width]'),
        pieces: this.querySelector('[data-personalization-pieces]')
      };

      this.maxChars = parseInt(this.dataset.maxChars, 10) || 20;
      this.charset = CHARSETS[this.dataset.charset] || CHARSETS.extended;
      this.plateWidth = parseFloat(this.dataset.plateWidth) || 250;
      this.capRatio = parseFloat(this.dataset.capRatio) || 0.72;

      // Absent attribute means on, so products configured before this setting
      // existed keep the disclosure. Only an explicit "false" turns it off.
      this.multiPiece = this.dataset.multiPiece !== 'false';

      if (this.previewWrap) this.previewWrap.hidden = false;

      // Colour picker. The "mixed" swatch reveals a free-text field for
      // per-letter instructions.
      //
      // The field is toggled with BOTH `hidden` and `disabled`, together. A
      // hidden-but-enabled input still submits its value, so a customer who
      // typed "V blue, O cream", then changed their mind and picked plain Red,
      // would have sent production a single colour AND a contradictory
      // per-letter note on the same line item. Disabling is what actually
      // keeps it off the order.
      this.colourInputs = Array.from(this.querySelectorAll('[data-colour-input]'));
      this.colourNote = this.querySelector('[data-colour-note]');
      this.colourNoteInput = this.querySelector('[data-colour-note-input]');

      if (this.colourInputs.length && this.colourNote && this.colourNoteInput) {
        this.onColourChange = () => {
          const mixed = this.colourInputs.some((i) => i.checked && i.dataset.mixed === 'true');
          this.colourNote.hidden = !mixed;
          this.colourNoteInput.disabled = !mixed;
          if (!mixed) this.colourNoteInput.value = '';
        };
        this.colourInputs.forEach((i) => i.addEventListener('change', this.onColourChange));
        // Run once: a browser restoring form state on back-navigation can
        // arrive with "mixed" already selected.
        this.onColourChange();
      }

      // Debounced so a fast typist does not trigger a layout measurement per
      // keystroke. 150ms is below the threshold at which the preview stops
      // feeling live.
      this.onInput = window.Theme.debounce(this.update.bind(this), DEBOUNCE_MS);
      this.input.addEventListener('input', this.onInput);

      // The rendered width depends on the chosen height, so a variant change
      // re-runs the measurement.
      this.unsubscribe = window.Theme.on('variant:changed', () => this.update());

      this.update();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.onColourChange && this.colourInputs) {
        this.colourInputs.forEach((i) => i.removeEventListener('change', this.onColourChange));
      }
    }

    /**
     * Collapses runs of whitespace and trims. The normalised value is what
     * gets printed; the raw value is kept in the visible property so a
     * customer's exact input is never silently rewritten without a record.
     */
    normalize(value) {
      return value.replace(/\s+/g, ' ').trim();
    }

    /**
     * @returns {{valid: boolean, message: string, route: boolean}}
     *   `route` means send them to a human rather than blocking outright.
     */
    validate(value) {
      if (!value) return { valid: true, message: '', route: false };

      if (EMOJI.test(value)) {
        return { valid: false, message: this.msg('emoji'), route: true };
      }

      // A letter that is not Latin means a script this font cannot render.
      // Showing tofu boxes, or stripping the characters, is how a customer
      // ends up confirming text that is not what they asked for — and with no
      // proof step after purchase, nothing downstream would catch it.
      const letters = Array.from(value).filter((ch) => ANY_LETTER.test(ch));
      const nonLatin = letters.some((ch) => !LATIN_LETTER.test(ch));
      if (nonLatin) {
        return { valid: false, message: this.msg('script'), route: true };
      }

      if (!this.charset.test(value)) {
        return { valid: false, message: this.msg('charset'), route: false };
      }

      return { valid: true, message: '', route: false };
    }

    msg(key) {
      return (window.Theme.strings.personalization && window.Theme.strings.personalization[key]) || '';
    }

    /**
     * Estimates the printed width in millimetres.
     *
     * `getComputedTextLength()` returns the rendered advance width in SVG user
     * units. Dividing by font-size gives a unitless width-per-em, which is
     * font-specific but height-independent. Multiplying by the physical cap
     * height converts it to millimetres.
     *
     * This is an ESTIMATE. It drives an informational notice and a hard ceiling
     * only — never a precise quote — because cap-height ratio varies by
     * typeface. `cap_height_ratio` is merchant-tunable for exactly that reason.
     *
     * @returns {number} Estimated width in mm, or 0 when it cannot be measured.
     */
    estimateWidthMm() {
      if (!this.svgText || typeof this.svgText.getComputedTextLength !== 'function') return 0;

      const heightIn = this.getSelectedHeightInches();
      if (!heightIn) return 0;

      let advance = 0;
      try {
        advance = this.svgText.getComputedTextLength();
      } catch (error) {
        // Firefox throws if the element is not rendered yet.
        return 0;
      }
      if (!advance) return 0;

      const fontSize = parseFloat(this.svgText.getAttribute('font-size')) || 72;
      const widthPerEm = advance / fontSize;
      const capHeightMm = heightIn * MM_PER_INCH;

      return (widthPerEm / this.capRatio) * capHeightMm;
    }

    /**
     * Reads the letter height from the selected variant option.
     * Matches 6", 6 in, 6in, 6 inch — merchants name these inconsistently and
     * a strict match would silently disable the width check.
     */
    getSelectedHeightInches() {
      const picker = document.querySelector('variant-picker');
      if (!picker) return 0;

      const checked = Array.from(picker.querySelectorAll('input:checked'));
      for (const input of checked) {
        const match = input.value.match(/(\d+(?:\.\d+)?)\s*(?:"|”|in\b|inch)/i);
        if (match) return parseFloat(match[1]);
      }
      return 0;
    }

    update() {
      const raw = this.input.value;
      const normalized = this.normalize(raw);
      const result = this.validate(raw);

      this.renderPreview(normalized);
      this.renderCounter(raw.length);
      this.renderError(result);

      const widthMm = result.valid ? this.estimateWidthMm() : 0;

      // Width is still measured and recorded when multi-piece is off — it is
      // useful to production either way. Only the SPLIT is skipped, because on
      // a per-letter product there is no plate for the text to overrun.
      const pieces =
        this.multiPiece && widthMm > 0 ? Math.max(1, Math.ceil(widthMm / this.plateWidth)) : 1;
      this.renderNotice(widthMm, pieces);

      this.writeProperties(normalized, widthMm, pieces);
      this.setSubmitState(result.valid);
    }

    renderPreview(value) {
      if (!this.svgText) return;

      this.svgText.textContent = value;

      // The accessible name mirrors the visible preview, so a screen-reader
      // user gets the same confirmation a sighted user does rather than an
      // unlabelled graphic.
      if (this.svgTitle) {
        this.svgTitle.textContent = value
          ? (this.msg('previewLabel') || '').replace('{{ text }}', value)
          : '';
      }

      // Grow the viewBox to fit rather than scaling the font, so long text
      // shrinks to fit the box instead of overflowing it.
      requestAnimationFrame(() => {
        let advance = 0;
        try {
          advance = this.svgText.getComputedTextLength();
        } catch (error) {
          return;
        }
        const width = Math.max(400, advance + 40);
        this.svg.setAttribute('viewBox', `0 0 ${Math.round(width)} 120`);
      });
    }

    renderCounter(length) {
      if (!this.counter) return;
      this.counter.textContent = `${length}/${this.maxChars}`;
      this.counter.classList.toggle('is-limit', length >= this.maxChars);
    }

    renderError(result) {
      if (!this.errorEl) return;

      if (result.valid) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        this.input.removeAttribute('aria-invalid');
        return;
      }

      this.errorEl.textContent = result.message;
      this.errorEl.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    renderNotice(widthMm, pieces) {
      if (!this.noticeEl || !this.noticeText) return;

      if (pieces > 1) {
        // Honest, pre-purchase, and framed as information rather than a
        // problem — this is normal manufacturing, not a fault.
        this.noticeText.textContent = (this.msg('multiPiece') || '')
          .replace('{{ pieces }}', pieces)
          .replace('{{ width }}', Math.round(widthMm));
        this.noticeEl.hidden = false;
      } else {
        this.noticeEl.hidden = true;
        this.noticeText.textContent = '';
      }
    }

    writeProperties(normalized, widthMm, pieces) {
      if (this.fields.normalized) this.fields.normalized.value = normalized;
      if (this.fields.charCount) this.fields.charCount.value = normalized.length;
      if (this.fields.width) this.fields.width.value = widthMm ? Math.round(widthMm) : '';
      if (this.fields.pieces) this.fields.pieces.value = pieces;
    }

    /**
     * Uses setCustomValidity rather than disabling the button.
     *
     * A disabled add-to-cart gives no explanation and cannot be focused, so a
     * customer is left tapping a dead control. An invalid field surfaces the
     * browser's own message, moves focus to the problem, and is announced.
     */
    setSubmitState(isValid) {
      if (!this.input) return;
      this.input.setCustomValidity(isValid ? '' : this.errorEl.textContent || 'Invalid');
    }
  }

  if (!customElements.get('product-personalization')) {
    customElements.define('product-personalization', Personalization);
  }
})();
